import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Stack, Typography } from '@rossum/ui/material';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';
import { RenderControlledTextInput } from '../../core/components/RenderControlledTextInput';
import { RenderDatapointLabel } from '../../core/components/RenderDatapointLabel';
import {
  convertErrorRateToPrecisionTarget,
  convertRateToPercentage,
  EMPTY_PLACEHOLDER,
  unifyDecimalSeparator,
} from '../../core/utils';
import { DifferenceChip } from '../components/DifferenceChip';
import { CustomPrecision } from '../types';

const commonColumnProps = {
  filterable: false,
};
export const CUSTOM_PRECISION_FIELD_CLASS_NAME = 'custom-precision-field';

export type OptimizationTableRowModel = {
  schemaId: string;
  estimatedErrorRate: number | null;
  newErrorRate?: number;
  errorRateDifference?: number;
  errorMessage?: string;
  datapointLabel: string;
};

/**
 * The only columns of the table that are shown in read-only mode: there is no
 * target to compare the current state against and nothing is editable.
 */
const READ_ONLY_COLUMN_FIELDS = ['datapointLabel', 'currentPrecision'];

export const useOptimizationTableColumns = ({
  readOnly,
}: {
  readOnly: boolean;
}) => {
  const intl = useIntl();

  const { watch, control } = useFormContext<CustomPrecision>();

  return useMemo<GridColDef<OptimizationTableRowModel>[]>(() => {
    const getCustomPrecisionRate = (schemaId: string) => {
      const stringValue = watch(schemaId);
      if (!stringValue) return null;
      const parsedCustomPrecisionRate = parseFloat(
        unifyDecimalSeparator(stringValue)
      );

      return parsedCustomPrecisionRate;
    };

    const columns: GridColDef<OptimizationTableRowModel>[] = [
      {
        ...commonColumnProps,
        flex: 1,
        field: 'datapointLabel',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimization.table.columns.fieldLabel',
        }),
        valueGetter: (_, row) => row.schemaId,
        renderCell: params => {
          return (
            <RenderDatapointLabel
              datapointLabel={params.row.datapointLabel}
              schemaId={params.row.schemaId}
            />
          );
        },
      },
      {
        ...commonColumnProps,
        width: readOnly ? 150 : undefined,
        field: 'currentPrecision',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimization.table.columns.currentPrecision',
        }),
        valueGetter: (_, row) =>
          typeof row.estimatedErrorRate === 'number'
            ? convertErrorRateToPrecisionTarget(row.estimatedErrorRate)
            : 0,
        renderCell: ({ row }) => {
          const value =
            typeof row.estimatedErrorRate === 'number'
              ? convertRateToPercentage(
                  convertErrorRateToPrecisionTarget(row.estimatedErrorRate)
                )
              : EMPTY_PLACEHOLDER;
          return (
            <Stack sx={{ height: 1, justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {value}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...commonColumnProps,
        sortable: false,
        field: 'customPrecision',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimization.table.columns.customPrecision',
        }),
        width: 150,
        renderCell: ({ row }) => {
          return (
            <RenderControlledTextInput
              control={control}
              schemaId={row.schemaId}
              errorMessage={row.errorMessage}
              className={CUSTOM_PRECISION_FIELD_CLASS_NAME}
            />
          );
        },
      },
      {
        ...commonColumnProps,

        field: 'newPrecision',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimization.table.columns.newPrecision',
        }),
        // allows for sorting
        valueGetter: (_, row) => {
          const { newErrorRate, errorMessage, schemaId } = row;
          const customPrecisionRate = getCustomPrecisionRate(schemaId);

          const customErrorRate =
            !errorMessage && customPrecisionRate
              ? 1 - customPrecisionRate / 100
              : null;
          const errorRate = customErrorRate ?? newErrorRate;

          return typeof errorRate === 'number'
            ? convertErrorRateToPrecisionTarget(errorRate)
            : 0;
        },
        renderCell: params => {
          const { newErrorRate, estimatedErrorRate, errorMessage } = params.row;

          const customPrecisionRate = getCustomPrecisionRate(
            params.row.schemaId
          );
          const customErrorRate =
            !errorMessage && customPrecisionRate
              ? 1 - customPrecisionRate / 100
              : null;
          const errorRate = customErrorRate ?? newErrorRate;

          const color =
            typeof errorRate === 'number' &&
            typeof estimatedErrorRate === 'number'
              ? errorRate > estimatedErrorRate
                ? 'warning'
                : errorRate < estimatedErrorRate
                  ? 'success'
                  : 'inherit'
              : null;

          const value =
            typeof errorRate === 'number'
              ? convertRateToPercentage(
                  convertErrorRateToPrecisionTarget(errorRate)
                )
              : EMPTY_PLACEHOLDER;

          return (
            <Stack
              sx={{
                height: 1,
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography
                variant="body2"
                color={color ?? 'inherit'}
                fontWeight={color ? 600 : 400}
              >
                {value}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...commonColumnProps,
        width: 120,
        field: 'precisionDifference',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimization.table.columns.precisionDifference',
        }),
        valueGetter: (_, row) => {
          const { newErrorRate, estimatedErrorRate, schemaId, errorMessage } =
            row;
          const customPrecisionRate = getCustomPrecisionRate(schemaId);
          const customErrorRate =
            !errorMessage && customPrecisionRate
              ? 1 - customPrecisionRate / 100
              : null;
          const errorRate = customErrorRate ?? newErrorRate;
          return typeof errorRate === 'number' &&
            typeof estimatedErrorRate === 'number'
            ? estimatedErrorRate - errorRate
            : 0;
        },
        renderCell: params => {
          const { newErrorRate, estimatedErrorRate, errorMessage } = params.row;
          const customPrecisionRate = getCustomPrecisionRate(
            params.row.schemaId
          );
          const customErrorRate =
            !errorMessage && customPrecisionRate
              ? 1 - customPrecisionRate / 100
              : null;
          const errorRate = customErrorRate ?? newErrorRate;

          const difference =
            typeof errorRate === 'number' &&
            typeof estimatedErrorRate === 'number'
              ? estimatedErrorRate - errorRate
              : null;

          return difference !== null ? (
            <Stack
              sx={{
                height: 1,
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              {difference === 0 ? (
                EMPTY_PLACEHOLDER
              ) : (
                <DifferenceChip difference={difference} />
              )}
            </Stack>
          ) : (
            EMPTY_PLACEHOLDER
          );
        },
      },
    ];

    return readOnly
      ? columns.filter(column => READ_ONLY_COLUMN_FIELDS.includes(column.field))
      : columns;
  }, [intl, control, watch, readOnly]);
};
